"use client";

import { useState } from "react";
import Icon from "@/components/ui/AppIcon";
import VehicleInfoForm, { type VehicleInfoData } from "./VehicleInfoForm";
import RegistrationDetailsForm, { type RegistrationDetailsData } from "./RegistrationDetailsForm";
import OwnerInfoForm, { type OwnerInfoData } from "./OwnerInfoForm";
import DocumentUploadForm, { type DocumentUploadData } from "./DocumentUploadForm";
import ReviewSubmitForm from "./ReviewSubmitForm";

export default function RegistrationWizard() {
  const [currentStep, setCurrentStep] = useState(0);
  const [vehicleInfo, setVehicleInfo] = useState<VehicleInfoData | null>(null);
  const [registrationDetails, setRegistrationDetails] = useState<RegistrationDetailsData | null>(null);
  const [ownerInfo, setOwnerInfo] = useState<OwnerInfoData | null>(null);
  const [documents, setDocuments] = useState<DocumentUploadData | null>(null);

  const steps = [
    { id: "step_vehicle", label: "Kjøretøyinfo", icon: "TruckIcon" },
    { id: "step_registration", label: "Registrering", icon: "DocumentTextIcon" },
    { id: "step_owner", label: "Eierinfo", icon: "UserIcon" },
    { id: "step_documents", label: "Dokumenter", icon: "FolderIcon" },
    { id: "step_review", label: "Gjennomgang", icon: "CheckCircleIcon" },
  ];

  const handleVehicleInfoSubmit = (data: VehicleInfoData) => {
    setVehicleInfo(data);
    setCurrentStep(1);
  };

  const handleRegistrationDetailsSubmit = (data: RegistrationDetailsData) => {
    setRegistrationDetails(data);
    setCurrentStep(2);
  };

  const handleOwnerInfoSubmit = (data: OwnerInfoData) => {
    setOwnerInfo(data);
    setCurrentStep(3);
  };

  const handleDocumentUploadSubmit = (data: DocumentUploadData) => {
    setDocuments(data);
    setCurrentStep(4);
  };

  const handleEditStep = (step: number) => {
    setCurrentStep(step);
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Progress Stepper */}
      <div className="mb-12">
        <div className="flex items-center justify-between relative">
          {/* Progress Line */}
          <div className="absolute top-6 left-0 right-0 h-0.5 bg-gray-200 -z-10">
            <div
              className="h-full bg-blue-800 transition-all duration-500"
              style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
            />
          </div>

          {/* Steps */}
          {steps.map((step, index) => (
            <div key={step.id} className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                  index < currentStep
                    ? "bg-blue-800 text-white"
                    : index === currentStep
                    ? "bg-blue-800 text-white ring-4 ring-blue-100" :"bg-white border-2 border-gray-300 text-gray-400"
                }`}
              >
                {index < currentStep ? (
                  <Icon name="CheckIcon" size={18} />
                ) : (
                  <Icon name={step.icon as any} size={18} />
                )}
              </div>
              <p
                className={`mt-3 text-sm font-medium transition-colors ${
                  index <= currentStep ? "text-gray-900" : "text-gray-500"
                }`}
              >
                {step.label}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Form Content */}
      <div className="bg-white rounded-lg p-8 shadow-clean-lg border border-gray-200 animate-scale-in">
        {currentStep === 0 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Kjøretøyinformasjon</h2>
            <p className="text-gray-600 mb-8">Skriv inn grunnleggende detaljer om kjøretøyet ditt</p>
            <VehicleInfoForm onNext={handleVehicleInfoSubmit} />
          </div>
        )}

        {currentStep === 1 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Registreringsdetaljer</h2>
            <p className="text-gray-600 mb-8">Oppgi registrerings- og overholdelseinformasjon</p>
            <RegistrationDetailsForm
              onNext={handleRegistrationDetailsSubmit}
              onBack={() => setCurrentStep(0)}
            />
          </div>
        )}

        {currentStep === 2 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Eierinformasjon</h2>
            <p className="text-gray-600 mb-8">Skriv inn kontakt- og adressedetaljer</p>
            <OwnerInfoForm
              onNext={handleOwnerInfoSubmit}
              onBack={() => setCurrentStep(1)}
            />
          </div>
        )}

        {currentStep === 3 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Last opp dokumenter</h2>
            <p className="text-gray-600 mb-8">Legg ved registrerings- og forsikringsbevis</p>
            <DocumentUploadForm
              onNext={handleDocumentUploadSubmit}
              onBack={() => setCurrentStep(2)}
            />
          </div>
        )}

        {currentStep === 4 && vehicleInfo && registrationDetails && ownerInfo && documents && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Gjennomgang og innsending</h2>
            <p className="text-gray-600 mb-8">Bekreft all informasjon før innsending</p>
            <ReviewSubmitForm
              vehicleInfo={vehicleInfo}
              registrationDetails={registrationDetails}
              ownerInfo={ownerInfo}
              documents={documents}
              onBack={() => setCurrentStep(3)}
              onEdit={handleEditStep}
            />
          </div>
        )}
      </div>
    </div>
  );
}